import React, { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, AlertTriangle, Settings as SettingsIcon, LogOut, PieChart, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuthStore, useLangStore } from "@/store/useStore";
import { adminUsersApi } from "@/services/api";
import { ChitLogo } from "@/components/ChitLogo";

const Badge = ({ count, variant = "dark" }) => {
  if (!count || count <= 0) return null;
  const cls =
    variant === "dark"
      ? "bg-[#EF4444] text-white"
      : "bg-[#EF4444] text-white ring-2 ring-white";
  return (
    <span
      className={`ml-auto min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold leading-[20px] text-center ${cls}`}
      data-testid="admin-pending-badge"
    >
      {count > 9 ? "9+" : count}
    </span>
  );
};

const NavItem = ({ to, icon: Icon, label, testid, badge }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    className={({ isActive }) =>
      `relative flex items-center gap-3 px-4 py-3 rounded-xl transition-colors group ${
        isActive
          ? "bg-white/[0.08] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]"
          : "text-white/70 hover:text-white hover:bg-white/[0.04]"
      }`
    }
  >
    {({ isActive }) => (
      <>
        {isActive && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-[#F5D78E]" />
        )}
        <Icon className={`h-5 w-5 ${isActive ? "text-[#F5D78E]" : "text-white/60 group-hover:text-white"}`} />
        <span className="text-sm font-medium">{label}</span>
        {badge}
      </>
    )}
  </NavLink>
);

const MobileItem = ({ to, icon: Icon, label, testid, count }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    className={({ isActive }) =>
      `relative flex-1 flex flex-col items-center justify-center gap-1 py-2 transition-colors ${
        isActive ? "text-[#1A3C6E]" : "text-[#6B7280]"
      }`
    }
  >
    <div className="relative">
      <Icon className="h-5 w-5" />
      {count > 0 && (
        <span
          className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[#EF4444] text-white text-[9px] font-bold leading-[16px] text-center ring-2 ring-white"
          data-testid="mnav-admin-badge"
        >
          {count > 9 ? "9+" : count}
        </span>
      )}
    </div>
    <span className="text-[11px] font-medium leading-tight">{label}</span>
  </NavLink>
);

export default function AppLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const { lang } = useLangStore();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);

  const isAdmin = user?.role === "admin";

  const loadPending = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await adminUsersApi.summary();
      setPendingCount(res.data?.counts?.pending_users || 0);
    } catch {
      // ignore
    }
  }, [isAdmin]);

  useEffect(() => {
    loadPending();
    if (!isAdmin) return;
    // Poll every 30s for new pending signups
    const id = setInterval(loadPending, 30000);
    // Refresh when user comes back to the tab
    const onFocus = () => loadPending();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [isAdmin, loadPending]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className={`min-h-screen bg-[#F2F4F8] ${lang === "te" ? "font-telugu" : ""}`} lang={lang}>
      <div className="mx-auto flex max-w-7xl">
        {/* Sidebar (desktop) — matches home hero navy+amber aesthetic */}
        <aside
          className="hidden md:flex md:w-64 md:flex-col md:min-h-screen md:sticky md:top-0 relative overflow-hidden bg-[#1A3C6E] text-white md:py-6 md:px-4"
          data-testid="app-sidebar"
        >
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-32 -right-24 h-[320px] w-[320px] rounded-full bg-[#6B3FA0]/30 blur-3xl" />
            <div className="absolute -bottom-40 -left-20 h-[280px] w-[280px] rounded-full bg-[#F59E0B]/10 blur-3xl" />
            <svg className="absolute inset-0 w-full h-full opacity-[0.06]" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="side-grid" width="28" height="28" patternUnits="userSpaceOnUse">
                  <path d="M 28 0 L 0 0 0 28" fill="none" stroke="white" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#side-grid)" />
            </svg>
          </div>

          <div className="relative z-10 flex flex-col h-full min-h-full">
            <div className="flex items-center gap-2.5 px-2 mb-9">
              <ChitLogo tone="light" className="h-10 w-10 shrink-0" />
              <span className="text-lg font-bold tracking-tight">{t("common.app_name")}</span>
            </div>

            <nav className="flex flex-col gap-1">
              <NavItem to="/dashboard" icon={LayoutDashboard} label={t("nav.dashboard")} testid="nav-dashboard" />
              <NavItem to="/defaulters" icon={AlertTriangle} label={t("nav.defaulters")} testid="nav-defaulters" />
              <NavItem to="/reports" icon={PieChart} label={t("nav.reports")} testid="nav-reports" />
              {isAdmin && (
                <NavItem
                  to="/admin"
                  icon={ShieldCheck}
                  label={t("nav.admin")}
                  testid="nav-admin"
                  badge={<Badge count={pendingCount} />}
                />
              )}
              <NavItem to="/settings" icon={SettingsIcon} label={t("nav.settings")} testid="nav-settings" />
            </nav>

            <div className="mt-auto pt-6">
              <div className="px-3 py-3 rounded-xl bg-white/[0.06] border border-white/10 backdrop-blur-sm mb-2">
                <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">
                  {t("settings.profile")}
                </div>
                <div
                  className="text-sm font-semibold truncate text-white mt-0.5"
                  data-testid="sidebar-user-name"
                >
                  {user?.name || "—"}
                </div>
                <div className="text-xs text-white/55 truncate">@{user?.username}</div>
              </div>
              <button
                onClick={handleLogout}
                data-testid="sidebar-logout-btn"
                className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-white/80 hover:text-[#FCA5A5] hover:bg-white/[0.04] transition-colors"
              >
                <LogOut className="h-4 w-4" /> {t("settings.logout")}
              </button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-h-screen pb-24 md:pb-6">
          <header className="md:hidden sticky top-0 z-20 bg-[#1A3C6E] text-white px-4 py-3 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <ChitLogo tone="light" className="h-9 w-9 shrink-0" />
              <span className="text-base font-bold tracking-tight">{t("common.app_name")}</span>
            </div>
          </header>

          {/* Admin inline banner — impossible to miss if there are pending signups */}
          {isAdmin && pendingCount > 0 && (
            <div className="px-4 md:px-8 pt-4">
              <button
                onClick={() => navigate("/admin")}
                data-testid="admin-pending-banner"
                className="w-full flex items-center gap-3 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-left hover:bg-[#FEF3C7] transition-colors"
              >
                <span className="relative h-9 w-9 rounded-full bg-[#F59E0B] text-white grid place-items-center shrink-0">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-bold leading-[18px] text-center ring-2 ring-[#FFFBEB]">
                    {pendingCount > 9 ? "9+" : pendingCount}
                  </span>
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-[#92400E]">
                    {t("admin_banner.title", { count: pendingCount })}
                  </div>
                  <div className="text-[11px] text-[#B45309]">
                    {t("admin_banner.cta")}
                  </div>
                </div>
                <span className="text-[11px] font-semibold text-[#B45309] hidden sm:inline">
                  {t("admin_banner.open")} →
                </span>
              </button>
            </div>
          )}

          <div className="px-4 md:px-8 py-5 md:py-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-200 flex items-center pb-safe">
        <MobileItem to="/dashboard" icon={LayoutDashboard} label={t("nav.dashboard")} testid="mnav-dashboard" count={0} />
        <MobileItem to="/defaulters" icon={AlertTriangle} label={t("nav.defaulters")} testid="mnav-defaulters" count={0} />
        <MobileItem to="/reports" icon={PieChart} label={t("nav.reports")} testid="mnav-reports" count={0} />
        {isAdmin && (
          <MobileItem to="/admin" icon={ShieldCheck} label={t("nav.admin")} testid="mnav-admin" count={pendingCount} />
        )}
        <MobileItem to="/settings" icon={SettingsIcon} label={t("nav.settings")} testid="mnav-settings" count={0} />
      </nav>
    </div>
  );
}
